import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { useNotificationFeed } from '../../../core/hooks/useNotificationFeed';
import { getHighlightFromFeedItem, isRecentlyTriggered, RECENT_TRIGGER_WINDOW_MS } from '../parse';

// Sobre a leitura compartilhada do feed (`useNotificationFeed`), aplica a lógica de risco: junta os ids que
// chegam ao vivo com os itens recentes (janela de 10 min, override por `?risk-recognition-window-time`) e
// devolve o primeiro item que carrega um highlight de risco renderizável (`{ item, highlight }`), ou null.
// Expõe `feedClient`/`feedItems` para as chamadas de engajamento (seen/archived/interacted) do controller.
// Os feed items do Knock são estruturalmente compatíveis com `RiskRecognitionFeedItem`, então passam direto.
const useRiskFeedHighlight = () => {
  const { feedClient, feedItems, realtimeItemIds } = useNotificationFeed();

  // Override de QA/debug: `?risk-recognition-window-time=<minutos>` alarga (ou encurta) a janela de
  // recência. Sem o param, ou com valor inválido/<=0, cai no default de 10 min.
  const [windowOverrideMinutes] = useQueryState('risk-recognition-window-time', parseAsInteger);
  const recentTriggerWindowMs = windowOverrideMinutes && windowOverrideMinutes > 0 ? windowOverrideMinutes * 60 * 1000 : RECENT_TRIGGER_WINDOW_MS;

  const riskRecognitionFeedItem = useMemo(() => {
    const nowMs = Date.now();
    for (const item of feedItems) {
      if (!(realtimeItemIds.has(item.id) || isRecentlyTriggered(item, nowMs, recentTriggerWindowMs))) continue;
      const highlight = getHighlightFromFeedItem(item);
      if (highlight) return { item, highlight };
    }

    return null;
  }, [feedItems, realtimeItemIds, recentTriggerWindowMs]);

  return { feedClient, feedItems, riskRecognitionFeedItem };
};

export default useRiskFeedHighlight;
